import { useState } from 'react';
import styled from 'styled-components';
import { updatePassword } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { IoEyeOutline, IoEyeOffOutline, IoCloseOutline, IoKeyOutline } from 'react-icons/io5';
import { auth, db } from '../../firebase/config';

const validatePw = (v) => v.length >= 8 && /[A-Za-z]/.test(v) && /\d/.test(v) && /[^A-Za-z0-9]/.test(v);

const ChangePasswordModal = ({ uid, onClose, onSuccess, dismissible = false }) => {
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validatePw(newPw)) return setError('새 비밀번호는 영문/숫자/기호 포함 8자 이상이어야 합니다.');
    if (newPw !== confirmPw) return setError('입력한 비밀번호가 일치하지 않습니다.');

    setLoading(true);
    try {
      await updatePassword(auth.currentUser, newPw);
      if (uid) {
        await updateDoc(doc(db, 'users', uid), { tempPassword: false });
      }
      onSuccess?.();
    } catch (err) {
      if (err.code === 'auth/requires-recent-login') {
        setError('보안을 위해 다시 로그인 후 시도해 주세요.');
      } else {
        setError(err.message || '비밀번호 변경에 실패했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={dismissible ? onClose : undefined}>
      <Box onClick={(e) => e.stopPropagation()}>
        {dismissible && (
          <CloseBtn type="button" onClick={onClose}>
            <IoCloseOutline size={22} />
          </CloseBtn>
        )}

        <IconCircle>
          <IoKeyOutline size={28} />
        </IconCircle>

        <Title>비밀번호 변경 필요</Title>
        <Desc>
          임시 비밀번호로 로그인하셨습니다.<br />
          보안을 위해 새 비밀번호를 설정해 주세요.
        </Desc>

        <form onSubmit={handleSubmit}>
          <Field>
            <Label>새 비밀번호</Label>
            <InputWrap>
              <Input
                type={showNew ? 'text' : 'password'}
                placeholder="영문/숫자/기호 포함 8자 이상"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password"
                $hasIcon
              />
              <EyeBtn type="button" onClick={() => setShowNew((v) => !v)}>
                {showNew ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
              </EyeBtn>
            </InputWrap>
          </Field>

          <Field>
            <Label>비밀번호 확인</Label>
            <InputWrap>
              <Input
                type={showConfirm ? 'text' : 'password'}
                placeholder="비밀번호를 다시 입력해 주세요"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
                $hasIcon
              />
              <EyeBtn type="button" onClick={() => setShowConfirm((v) => !v)}>
                {showConfirm ? <IoEyeOffOutline size={18} /> : <IoEyeOutline size={18} />}
              </EyeBtn>
            </InputWrap>
          </Field>

          {error && <ErrorMsg>{error}</ErrorMsg>}

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? '변경 중...' : '변경 적용'}
          </SubmitBtn>
        </form>
      </Box>
    </Overlay>
  );
};

export default ChangePasswordModal;

// ───── 스타일 ─────
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Box = styled.div`
  position: relative;
  width: 100%;
  max-width: 360px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
`;

const IconCircle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  margin: 0 auto ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primary}15;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
`;

const Title = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  display: flex;
  padding: 0;
`;

const Desc = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 0 0 ${({ theme }) => theme.spacing.lg};
`;

const Field = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  text-align: left;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const InputWrap = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  padding-right: ${({ $hasIcon }) => ($hasIcon ? '42px' : '14px')};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  outline: none;

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const EyeBtn = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: 0;
  color: ${({ theme }) => theme.colors.textLight};
  display: flex;
  padding: 4px;
  cursor: pointer;
`;

const ErrorMsg = styled.p`
  color: ${({ theme }) => theme.colors.danger};
  font-size: 12px;
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
